import React from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { Box, Chip, IconButton, Link, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { translate } from 'translations/translator';
import { AppStoreState, Assignment } from 'types';
import { AssignmentFilters } from './Filters';
import { SATableHeader } from './SATableHeader';
import { SATableLayout } from './SATableLayout';
import { formatUiDate } from 'util/date-util';
import { TableColumnProps } from '@lwtears/lwt-common-frontend/lib/Table';

interface Props {
  assignments: Assignment[];
  filters: AssignmentFilters;
  onAddAssignmentClick: () => void;
}

const getTranslation = (key, options?) => translate('assignmentTracker', key, options);

const getDateValue = (path: string) => (assignment: Assignment) => {
  const date = formatUiDate(new Date(get(assignment, path)));
  return <Typography variant="body1">{date}</Typography>;
};

export const ClassroomAssignmentList: React.FC<Props> = ({
  assignments,
  filters,
  onAddAssignmentClick
}) => {
  const theme = useTheme();
  const classes = useStyles(theme)();
  const navigate = useNavigate();

  const classroomsById = useSelector(({ entity }: AppStoreState) => entity.classroomsById) ?? {};

  const handlePreviousClick = () => navigate(-1);

  const getAssignedValue = (assignment: Assignment) => {
    const { classroomId, studentIds } = assignment;
    const classroomName = classroomsById[classroomId]?.name;
    const assignedText = studentIds
      ? getTranslation('assignedStudents', { assignedCount: studentIds.length, classroomName })
      : classroomName;

    return <Typography variant="body1">{assignedText}</Typography>;
  };

  const getMessageCountValue = (assignment: Assignment): React.ReactElement => {
    if (!assignment.unreadMessageCount) {
      return <Typography variant="body1">{getTranslation('noNewMessages')}</Typography>;
    }

    return (
      <Chip
        size="medium"
        classes={{ label: classes.labelChip, root: classes.messageChip }}
        label={`${assignment.unreadMessageCount} ${getTranslation('newMessage')}`}
      />
    );
  };

  const columns: TableColumnProps<any>[] = [
    {
      label: getTranslation('title'),
      getValue(assignment: Assignment): React.ReactElement {
        return (
          <Link
            component={RouterLink}
            to={`assignments/${assignment.id}`}
            variant="h6"
            className={classes.link}
          >
            {assignment.title}
          </Link>
        );
      },
      options: {
        width: '25%',
        sortable: true,
        sortKey: 'title'
      }
    },
    {
      label: getTranslation('dateAssigned'),
      getValue: getDateValue('dateAssigned'),
      options: {
        width: '15%',
        sortable: true,
        sortKey: 'dateAssigned'
      }
    },
    {
      label: getTranslation('dateDue'),
      getValue: getDateValue('dateDue'),
      options: {
        width: '15%',
        sortable: true,
        sortKey: 'dateDue'
      }
    },
    {
      label: getTranslation('assignedTo'),
      getValue: getAssignedValue,
      options: {
        width: '21%',
        sortable: false
      }
    },
    {
      label: getTranslation('messages'),
      getValue: getMessageCountValue,
      options: {
        width: '21%',
        sortable: true,
        sortKey: 'unreadMessageCount'
      }
    },
    {
      label: '',
      getValue(assignment: Assignment): React.ReactElement {
        return (
          <Box display="flex" alignItems="center" fontWeight="fontWeightBold">
            <RouterLink to={`assignments/${assignment.id}`}>
              <IconButton key={`icon-button-${assignment.id}`}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </RouterLink>
          </Box>
        );
      },
      options: {
        width: '3%',
        sortable: false
      }
    }
  ];

  return (
    <Box py={3} px={5} bgcolor="primary.light" className={classes.container}>
      <SATableHeader
        title={getTranslation('tableTitle')}
        renderBackArrow={false}
        handlePreviousClick={handlePreviousClick}
      />
      <SATableLayout
        data={assignments}
        columns={columns}
        filters={[{ path: 'classroomId', compareTo: filters.classroomId }]}
        titleNoData={getTranslation('titleNoData')}
        subTitleNoData={getTranslation('subTitleNoData')}
        buttonTitleNoData={getTranslation('buttonTitleNoData')}
        handleNoDataClick={onAddAssignmentClick}
      />
    </Box>
  );
};

export default ClassroomAssignmentList;

/*
    124px = HeightFooter + HeightHeader
*/
const footerAndHeaderHeight = 150;
const filtersHeight = 104;
const useStyles = theme =>
  makeStyles(() => ({
    container: {
      height: `calc(100vh - ${footerAndHeaderHeight + filtersHeight}px)`,
      overflow: 'auto'
    },
    messageChip: {
      backgroundColor: '#D12348'
    },
    link: () => ({
      color: `${theme.palette.text.primary} !important`,
      textDecoration: 'none !important',
      '&:hover': { color: 'inherit', textDecoration: 'underline !important'  }
    }),
    labelChip: {
      color: '#FFF',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 400
    }
  }));
